/**
 * A practice's premium lens price list as the settings screen edits it - one entry per lens and type -
 * turned into prices by lens (form.prices[lens.key].toric / .non_toric, null where there is none) and back.
 *
 * Saving replaces the whole list, so an entry the screen doesn't show (standard monofocal, another lens type)
 * is carried back as it was rather than dropped.
 */
app.factory("PremiumLensPriceList", [
	"PREMIUM_LENSES",
	function (PREMIUM_LENSES) {
		// The API's limit on a lens price
		const MAX_PRICE = 4000;
		const SLOTS = [
			{ key: "non_toric", type: "NON_TORIC" },
			{ key: "toric", type: "TORIC" },
		];

		function hasPrice(value) {
			return value !== null && value !== undefined && value !== "";
		}

		function isValidPrice(price) {
			return typeof price === "number" && !isNaN(price) && price >= 0 && price <= MAX_PRICE;
		}

		// The toric monofocals' non-toric is included as standard, so it is never priced here
		function isPriced(lens, slot) {
			return !(lens.toricOnly && slot.key === "non_toric");
		}

		function emptyPrices() {
			const prices = {};
			PREMIUM_LENSES.forEach((lens) => (prices[lens.key] = { toric: null, non_toric: null }));
			return prices;
		}

		/**
		 * A saved list as prices by lens. `ids` keeps each price's id so it is saved under it again;
		 * `unlisted` the entries no screen shows, to send back as they are.
		 */
		function fromList(entries) {
			const prices = emptyPrices();
			const ids = {};
			const unlisted = [];
			(entries || []).forEach((entry) => {
				const lens = PREMIUM_LENSES.find((l) => l.name === entry.lens_name);
				const slot = SLOTS.find((s) => s.type === entry.lens_type);
				const listed = lens && slot && isPriced(lens, slot);
				if (listed && prices[lens.key][slot.key] === null) {
					prices[lens.key][slot.key] = entry.lens_price;
					ids[lens.key + ":" + slot.key] = entry.premium_lens_id;
				} else if (!listed && entry.lens_name && entry.lens_type && isValidPrice(entry.lens_price)) {
					// The API refuses an entry without a valid price, which would fail the whole save; an
					// unlisted one in that state could never be used anyway.
					unlisted.push(entry);
				}
			});
			return { prices: prices, ids: ids, unlisted: unlisted };
		}

		/** Prices by lens back into a list - empty prices left out - followed by the unlisted entries. */
		function toList(prices, ids, unlisted) {
			const list = [];
			PREMIUM_LENSES.forEach((lens) => {
				SLOTS.forEach((slot) => {
					const value = prices[lens.key][slot.key];
					if (!isPriced(lens, slot) || !hasPrice(value)) {
						return;
					}
					const entry = {
						lens_name: lens.name,
						lens_type: slot.type,
						// To the cent, as a patient is quoted it
						lens_price: Math.round(value * 100) / 100,
					};
					const id = ids && ids[lens.key + ":" + slot.key];
					if (id) {
						entry.premium_lens_id = id;
					}
					list.push(entry);
				});
			});
			return list.concat(unlisted || []);
		}

		function isPriceInvalid(value) {
			return hasPrice(value) && !isValidPrice(value);
		}

		/** What stops these prices being saved, in words; empty when nothing does. */
		function priceProblem(prices) {
			const bad = PREMIUM_LENSES.some((lens) => SLOTS.some((slot) => isPriceInvalid(prices[lens.key][slot.key])));
			return bad ? "Prices must be between $0 and $" + MAX_PRICE.toLocaleString() + "." : "";
		}

		/** How many prices are set. */
		function count(prices) {
			return PREMIUM_LENSES.reduce((total, lens) => total + SLOTS.filter((slot) => isPriced(lens, slot) && hasPrice(prices[lens.key][slot.key])).length, 0);
		}

		/** A price as a person reads it: $1,800 or $1,799.50. */
		function format(value) {
			const cents = Math.round(value * 100) % 100 !== 0;
			return "$" + Number(value).toLocaleString("en-NZ", { minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: 2 });
		}

		/** The reason the API gave, where it gave one a person can act on. */
		function errorMessage(resp, fallback) {
			const message = resp && resp.data && resp.data.message;
			const reason = message && /(?:CattraxServiceException|BadRequestException): (.*?)\]?$/.exec(message);
			if (reason) {
				return reason[1];
			}
			return resp && resp.status === 403 ? "You don't have permission to do that." : fallback;
		}

		return {
			MAX_PRICE: MAX_PRICE,
			SLOTS: SLOTS,
			hasPrice: hasPrice,
			isValidPrice: isValidPrice,
			isPriceInvalid: isPriceInvalid,
			emptyPrices: emptyPrices,
			fromList: fromList,
			toList: toList,
			priceProblem: priceProblem,
			count: count,
			format: format,
			errorMessage: errorMessage,
		};
	},
]);
